import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ChevronUp, ChevronDown, Users } from 'lucide-react';

/**
 * Utilization Details Card showing per-employee utilization with expandable calculation details
 */
const UtilizationDetails = ({ 
  employees,
  showDetails,
  setShowDetails,
  expandedCalcDetails,
  toggleCalcDetails,
  filterEmployee,
  filterProject,
  planningSettings,
  getStatusBadge
}) => {
  if (!employees || employees.length === 0) return null;

  return (
    <Card>
      <CardHeader 
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setShowDetails(!showDetails)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            Auslastungsdetails
            {(filterEmployee || filterProject) && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                gefiltert
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {employees.length} Mitarbeiter
            </span>
            {showDetails ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </div>
      </CardHeader>
      {showDetails && (
        <CardContent>
          <div className="space-y-4">
            {employees.map((emp) => (
              <div key={emp.user_id} className="rounded-lg border">
                <div className="flex items-center gap-4 p-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-medium">
                    {emp.user_name?.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{emp.user_name}</span>
                      {getStatusBadge(emp.status, emp.status_label)}
                    </div>
                    <Progress 
                      value={Math.min(emp.utilization_percent, 100)} 
                      className={`h-2 ${emp.status === 'overbooked' ? '[&>div]:bg-red-500' : ''}`}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>{emp.allocated_hours}h allokiert</span>
                      <span>{emp.available_hours}h verfügbar von {emp.total_available_project_hours}h Projekt</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button 
                            onClick={() => toggleCalcDetails(emp.user_id)}
                            className="hover:bg-muted/50 rounded-lg p-2 transition-colors cursor-pointer"
                          >
                            <p className={`text-2xl font-bold ${emp.status === 'overbooked' ? 'text-red-600' : ''}`}>
                              {emp.utilization_percent}%
                            </p>
                            <p className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                              Auslastung
                              {expandedCalcDetails[emp.user_id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                            </p>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Klicken für Berechnungsdetails</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                
                {/* Expandable Calculation Details */}
                {expandedCalcDetails[emp.user_id] && (
                  <div className="px-4 pb-4 pt-2 border-t bg-muted/30">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Berechnung:</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Wochenstunden:</span>
                        <span className="font-mono">{emp.weekly_hours}h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Arbeitstage im Zeitraum:</span>
                        <span className="font-mono">{emp.working_days} Tage</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tageskapazität ({emp.weekly_hours}h ÷ 5):</span>
                        <span className="font-mono">{emp.daily_hours}h/Tag</span>
                      </div>
                      <div className="flex justify-between border-t pt-1 mt-1">
                        <span className="text-muted-foreground">Bruttokapazität ({emp.working_days} × {emp.daily_hours}h):</span>
                        <span className="font-mono">{emp.total_capacity_hours}h</span>
                      </div>
                      <div className="flex justify-between text-green-600">
                        <span>− Abwesenheiten:</span>
                        <span className="font-mono">-{emp.absence_hours}h</span>
                      </div>
                      <div className="flex justify-between font-medium border-t pt-1 mt-1">
                        <span>= Effektive Kapazität:</span>
                        <span className="font-mono">{emp.effective_capacity_hours}h</span>
                      </div>
                      <div className="flex justify-between text-blue-600">
                        <span>→ davon Projekt ({planningSettings?.project_work_percent || 70}%):</span>
                        <span className="font-mono">{emp.total_available_project_hours}h</span>
                      </div>
                      <div className="flex justify-between border-t pt-1 mt-1">
                        <span className="text-muted-foreground">Allokierte Projekt-Stunden:</span>
                        <span className="font-mono">{emp.allocated_hours}h</span>
                      </div>
                      <div className={`flex justify-between font-medium p-2 rounded mt-2 ${emp.status === 'overbooked' ? 'text-red-600 bg-red-50 dark:bg-red-900/30' : 'text-blue-600 bg-blue-50 dark:bg-blue-900/30'}`}>
                        <span>Auslastung ({emp.allocated_hours}h ÷ {emp.total_available_project_hours}h × 100):</span>
                        <span className="font-mono font-bold">{emp.utilization_percent}%</span>
                      </div>
                      {emp.available_hours > 0 && (
                        <div className="flex justify-between text-green-600 dark:text-green-400 mt-1">
                          <span>Noch verfügbar:</span>
                          <span className="font-mono">{emp.available_hours}h</span>
                        </div>
                      )}
                      {emp.utilization_percent > 100 && (
                        <div className="flex justify-between text-red-600 dark:text-red-400 mt-1">
                          <span>⚠️ Überbucht um:</span>
                          <span className="font-mono">
                            {(emp.allocated_hours - emp.total_available_project_hours).toFixed(1)}h
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {employees.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>Keine Mitarbeiter für den gewählten Filter</p>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default UtilizationDetails;
